/**
 * Versioned save data in IndexedDB.
 *
 * Migrations exist from the first version deliberately: playtest saves outlive
 * schema changes, and retrofitting migration onto a format already in the wild
 * means either breaking saves or writing the migration anyway under pressure.
 */

const DB_NAME = 'heartland';
const DB_VERSION = 1;
const STORE = 'saves';
const SLOT = 'primary';

export const CURRENT_SAVE_VERSION = 15;

export interface SaveDataV1 {
  readonly version: 1;
  readonly player: { readonly x: number; readonly y: number; readonly facing: string };
  readonly map: string;
  /** Wall-clock milliseconds, for "last played" display. */
  readonly savedAt: number;
}

export interface SaveDataV2 {
  readonly version: 2;
  readonly player: { readonly x: number; readonly y: number; readonly facing: string };
  /** Area the player is standing in. */
  readonly map: string;
  /** Game minutes since the start of day 1. */
  readonly totalMinutes: number;
  /** World seed, which the weather for any day is derived from. */
  readonly seed: number;
  readonly savedAt: number;
}

export interface SaveDataV3 {
  readonly version: 3;
  readonly player: { readonly x: number; readonly y: number; readonly facing: string };
  readonly map: string;
  readonly totalMinutes: number;
  readonly seed: number;
  /** Tilled plots and their crops. */
  readonly farmland: unknown[];
  /** Hotbar contents and which slot is in hand. */
  readonly inventory: unknown[];
  readonly selectedSlot: number;
  readonly savedAt: number;
}

export interface SaveDataV4 extends Omit<SaveDataV3, 'version'> {
  readonly version: 4;
  /** Animals and what they are owed. */
  readonly livestock: unknown[];
  /** Placed furniture, keyed by area. */
  readonly furniture: Record<string, unknown>;
}

export interface SaveDataV5 extends Omit<SaveDataV4, 'version'> {
  readonly version: 5;
  /** Tools carried and which is in hand. */
  readonly belt: unknown;
}

export interface SaveDataV6 extends Omit<SaveDataV5, 'version'> {
  readonly version: 6;
  /** Quick-access assignments: item ids, not items. See world/quickBag.ts. */
  readonly quickBag: (string | null)[];
}

export interface SaveDataV7 extends Omit<SaveDataV6, 'version'> {
  readonly version: 7;
  /** Items left lying on the ground, keyed by area. */
  readonly ground: Record<string, unknown>;
}

export interface SaveDataV8 extends Omit<SaveDataV7, 'version'> {
  readonly version: 8;
  /** Damage dealt per weapon, which is what a weapon's level is made of. */
  readonly weaponGrowth: Record<string, number>;
}

export interface SaveDataV9 extends Omit<SaveDataV8, 'version'> {
  readonly version: 9;
  /** Abilities absorbed into each weapon. */
  readonly weaponAbilities: Record<string, string[]>;
}

export interface SaveDataV10 extends Omit<SaveDataV9, 'version'> {
  readonly version: 10;
  /** Coins. Every item has carried a price since v3 with nothing to spend. */
  readonly coins: number;
}

export interface SaveDataV11 extends Omit<SaveDataV10, 'version'> {
  readonly version: 11;
  /** How well each person knows you. Cannot be derived from anything. */
  readonly friendships: Record<string, unknown>;
}

export interface SaveDataV12 extends Omit<SaveDataV11, 'version'> {
  readonly version: 12;
  /**
   * Which of today's requests are already filled.
   *
   * The postings themselves derive from the seed and the day, so only the
   * part that cannot be derived is written down.
   */
  readonly requests: Record<string, unknown>;
}

export interface SaveDataV13 extends Omit<SaveDataV12, 'version'> {
  readonly version: 13;
  /** How far through each quest. A chain is a history, not a state of the world. */
  readonly quests: Record<string, unknown>;
}

export interface SaveDataV14 extends Omit<SaveDataV13, 'version'> {
  readonly version: 14;
  /** Festivals already entered, keyed by year: the day itself derives from the date. */
  readonly festivals: string[];
}

export interface SaveDataV15 extends Omit<SaveDataV14, 'version'> {
  readonly version: 15;
  /**
   * Floors below the surface, zero above ground.
   *
   * The floor itself is not saved — it derives from (seed, depth) — so this
   * one number is the whole of "where underground you were".
   */
  readonly depth: number;
  /** Where the descent started, so climbing out still leads somewhere. */
  readonly surfaceReturn: { area: string; x: number; y: number } | null;
}

export type SaveData = SaveDataV15;

/** Anything read off disk: shape unknown until migrated and validated. */
export type UnknownSave = { version?: unknown } & Record<string, unknown>;

type Migration = (save: UnknownSave) => UnknownSave;

/**
 * Keyed by the version being migrated *from*. Applied in sequence until the
 * save reaches CURRENT_SAVE_VERSION, so a v1 save can reach v4 unattended.
 */
export const MIGRATIONS: Readonly<Record<number, Migration>> = {
  // v1 predates the clock and the weather seed. Existing saves resume at the
  // start of day 1 with a fresh seed rather than being discarded.
  1: (save) => ({
    ...save,
    version: 2,
    totalMinutes: DEFAULT_START_MINUTES,
    seed: randomSeed(),
  }),
  // v2 predates farming. An existing playthrough keeps its position, clock and
  // seed, and starts with an untouched farm and the standard kit.
  2: (save) => ({
    ...save,
    version: 3,
    farmland: [],
    inventory: [],
    selectedSlot: 0,
  }),
  // v3 predates animals and furniture. The coop starts with its usual two
  // chickens; nothing about an existing farm changes.
  3: (save) => ({ ...save, version: 4, livestock: [], furniture: {} }),
  // v4 kept tools in the bag. The belt rebuilds itself from nothing with the
  // basics, and any rods still sitting in the saved bag are picked up by
  // ToolBelt.fromSave being permissive about what it is handed.
  4: (save) => ({ ...save, version: 5, belt: null }),
  // v5 had no separate bag: the backpack's first eight slots *were* the
  // quick-access row. Those eight become the assignments, so a playthrough
  // keeps exactly the loadout it had, and the backpack keeps every item —
  // nothing moves, the bar just stops being the same thing as storage.
  5: (save) => {
    const inventory = Array.isArray(save.inventory) ? save.inventory : [];
    const quickBag = inventory.slice(0, 8).map((entry) => {
      const id = (entry as { id?: unknown } | null)?.id;
      return typeof id === 'string' ? id : null;
    });
    return { ...save, version: 6, quickBag };
  },
  // v6 predates dropping things. Every existing farm simply has a clean floor.
  6: (save) => ({ ...save, version: 7, ground: {} }),
  // v7 predates weapons growing with use. An existing blade starts at level
  // one — awarding history nobody earned would be worse than starting fresh.
  7: (save) => ({ ...save, version: 8, weaponGrowth: {} }),
  // v8 predates absorption. Existing blades start empty, with their materials
  // still in the bag to spend however the player decides.
  8: (save) => ({ ...save, version: 9, weaponAbilities: {} }),
  // v9 predates money. An existing farm starts broke, which is where a new
  // one starts too — nothing was ever earned, so nothing is owed.
  9: (save) => ({ ...save, version: 10, coins: 0 }),
  // v10 predates anyone to know. Everyone starts a stranger, which is where a
  // new farm starts too.
  10: (save) => ({ ...save, version: 11, friendships: {} }),
  // v11 predates the board. An empty record reads as "no day recorded", which
  // is the same state a fresh farm wakes up in.
  11: (save) => ({ ...save, version: 12, requests: {} }),
  // v12 predates anyone asking for anything. Nothing started, which is where
  // a new farm starts too.
  12: (save) => ({ ...save, version: 13, quests: {} }),
  // v13 predates the calendar having anything on it. Nothing entered, and the
  // next festival comes round on its own.
  13: (save) => ({ ...save, version: 14, festivals: [] }),
  // v14 could not describe being underground, so every v14 save is a save
  // above ground — which is exactly what depth zero means.
  14: (save) => ({ ...save, version: 15, depth: 0, surfaceReturn: null }),
};

export function migrate(save: UnknownSave): UnknownSave {
  let current = save;
  let guard = 0;

  while (typeof current.version === 'number' && current.version < CURRENT_SAVE_VERSION) {
    const migration = MIGRATIONS[current.version];
    if (!migration) {
      throw new Error(`No migration from save version ${current.version}`);
    }
    const next = migration(current);
    if (typeof next.version !== 'number' || next.version <= (current.version)) {
      throw new Error(`Migration from version ${current.version} did not advance the version`);
    }
    current = next;

    // A malformed migration table must fail loudly rather than hang the boot.
    if (++guard > 100) throw new Error('Migration loop did not terminate');
  }

  return current;
}

/** Structural validation. Corrupt saves are discarded rather than crashing. */
export function isValidSave(value: UnknownSave): value is SaveData & UnknownSave {
  if (value.version !== CURRENT_SAVE_VERSION) return false;
  const player = value.player as Record<string, unknown> | undefined;
  return (
    typeof value.map === 'string' &&
    typeof value.savedAt === 'number' &&
    typeof value.totalMinutes === 'number' &&
    Number.isFinite(value.totalMinutes) &&
    value.totalMinutes >= 0 &&
    typeof value.seed === 'number' &&
    Number.isFinite(value.seed) &&
    Array.isArray(value.farmland) &&
    Array.isArray(value.inventory) &&
    typeof value.selectedSlot === 'number' &&
    Array.isArray(value.quickBag) &&
    typeof value.ground === 'object' &&
    value.ground !== null &&
    typeof value.weaponGrowth === 'object' &&
    value.weaponGrowth !== null &&
    typeof value.weaponAbilities === 'object' &&
    value.weaponAbilities !== null &&
    typeof value.coins === 'number' &&
    typeof value.friendships === 'object' &&
    value.friendships !== null &&
    typeof value.requests === 'object' &&
    value.requests !== null &&
    typeof value.quests === 'object' &&
    value.quests !== null &&
    Array.isArray(value.festivals) &&
    typeof value.depth === 'number' &&
    Number.isFinite(value.depth) &&
    Array.isArray(value.livestock) &&
    'belt' in value &&
    typeof value.furniture === 'object' &&
    value.furniture !== null &&
    !!player &&
    typeof player.x === 'number' &&
    typeof player.y === 'number' &&
    typeof player.facing === 'string' &&
    Number.isFinite(player.x) &&
    Number.isFinite(player.y)
  );
}

/** 6am on day 1, matching the clock's own default. */
export const DEFAULT_START_MINUTES = 6 * 60;

/** A world seed for a new game. Only its value matters, not its quality. */
export function randomSeed(): number {
  return Math.floor(Math.random() * 0x7fffffff);
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('Failed to open save database'));
    };
  });
}

export async function writeSave(data: SaveData): Promise<void> {
  const db = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(data, SLOT);
      tx.oncomplete = () => {
        resolve();
      };
      tx.onerror = () => {
        reject(tx.error ?? new Error('Save write failed'));
      };
    });
  } finally {
    db.close();
  }
}

/**
 * Returns null when there is no save, or when the stored data is unreadable.
 * A corrupt save must not brick the game — starting fresh beats a white screen.
 */
export async function readSave(): Promise<SaveData | null> {
  let raw: unknown;

  try {
    const db = await openDatabase();
    try {
      raw = await new Promise<unknown>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readonly');
        const request = tx.objectStore(STORE).get(SLOT);
        request.onsuccess = () => {
          resolve(request.result);
        };
        request.onerror = () => {
          reject(request.error ?? new Error('Save read failed'));
        };
      });
    } finally {
      db.close();
    }
  } catch (error) {
    console.warn('Could not read save data; starting fresh.', error);
    return null;
  }

  if (!raw || typeof raw !== 'object') return null;

  try {
    const migrated = migrate(raw as UnknownSave);
    return isValidSave(migrated) ? (migrated) : null;
  } catch (error) {
    console.warn('Save data could not be migrated; starting fresh.', error);
    return null;
  }
}
